import api from './api'
import type { 
  OversightPolygonPayload, 
  OversightPointPayload, 
  OversightEntity, 
  Lock, 
  FlightNetworkStatus,
  FlightNetworkValidationError,
  OversightFlightNetwork,
} from './types'

const FLIGHT_NETWORKS_RESOURCE = 'flight_networks'
const ENTITIES_RESOURCE = 'entities'
const LOCK_RESOURCE = 'lock'
const DUMP_RESOURCE = 'dump'

type WithLock = { lock: Lock }
type Payload = {
  partial_publishing: boolean,
  polygons: OversightPolygonPayload[],
  points: OversightPointPayload[],
} & WithLock

const getFlightNetwork = async (slug: string, status: FlightNetworkStatus) => {
  return await api.get<OversightFlightNetwork>(`${FLIGHT_NETWORKS_RESOURCE}/${slug}?status=${status}`)
}

const publishFlightNetwork = async (slug: string, entities: Payload) => {
  return await api.post<WithLock, { errors?: FlightNetworkValidationError[] }>(`${FLIGHT_NETWORKS_RESOURCE}/${slug}/publish`, entities)
}

const validate = async (slug: string, entities: Payload) => {
  return await api.post<Payload, { errors: FlightNetworkValidationError[] }>(`${FLIGHT_NETWORKS_RESOURCE}/${slug}/validate`, entities)
}

const getEntities = async (slug: string, status: FlightNetworkStatus) => {
  return await api.get<OversightEntity[]>(`${FLIGHT_NETWORKS_RESOURCE}/${slug}/${ENTITIES_RESOURCE}/`, { status })
}

const postEntities = async (slug: string, entities: Payload, status: FlightNetworkStatus) => {
  return await api.post<Payload>(`${FLIGHT_NETWORKS_RESOURCE}/${slug}/${ENTITIES_RESOURCE}/`, entities, { status })
}

const deleteEntities = async (slug: string, body: WithLock, status: FlightNetworkStatus) => {
  return await api.delete<WithLock>(`${FLIGHT_NETWORKS_RESOURCE}/${slug}/${ENTITIES_RESOURCE}/`, body, { status })
}

const getLock = async (slug: string) => {
  return await api.get<Lock>(`${FLIGHT_NETWORKS_RESOURCE}/${slug}/${LOCK_RESOURCE}/`)
}

const postLock = async (slug: string, lock: Lock) => {
  return await api.post<Lock, Lock>(`${FLIGHT_NETWORKS_RESOURCE}/${slug}/${LOCK_RESOURCE}/`, lock)
}

const deleteLock = async (slug: string, lock: Lock) => {
  return await api.delete<Lock, Lock>(`${FLIGHT_NETWORKS_RESOURCE}/${slug}/${LOCK_RESOURCE}/`, lock)
}

const downloadDump = async (slug: string, status: FlightNetworkStatus, lock: Lock) => {
  const encodedLock = encodeURIComponent(JSON.stringify(lock))

  return await api.directFetch(`${FLIGHT_NETWORKS_RESOURCE}/${slug}/${DUMP_RESOURCE}/?status=${status}&lock=${encodedLock}`, { method: 'GET' })
}

// It can only update draft flight network
const uploadDump = async (slug: string, file: File, lock: Lock, newUuids = false) => {
  const formData = new FormData()

  formData.append('flight_network', file)
  formData.append('new_uuids', String(newUuids))
  formData.append('partial_publishing', String(true))

  const encodedLock = encodeURIComponent(JSON.stringify(lock))

  return await api.directFetch(`${FLIGHT_NETWORKS_RESOURCE}/${slug}/${DUMP_RESOURCE}/?&lock=${encodedLock}`, {
    method: 'POST',
    body: formData,
  })
}

export {
  getFlightNetwork,
  publishFlightNetwork,
  getEntities,
  postEntities,
  deleteEntities,
  getLock,
  postLock,
  deleteLock,
  validate,
  downloadDump,
  uploadDump
}